import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Eraser, Save, Search, X } from "lucide-react";
import { createOvertimeEntry, getOvertimePeriodStatus } from "../../../api/overtimeApi";

const getLocalDate = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const errorMessage = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.msg ||
  error?.response?.data?.message ||
  "Kayıt oluşturulamadı.";

const SAVE_CONCURRENCY = 4;
const SUGGESTION_LIMIT = 12;
const DISTRIBUTION_CHIP_LIMIT = 10;

const formatHours = (value) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(Number(value || 0));

const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString("tr-TR", { timeZone: "UTC" }) : "";

const formatMonth = (periodKey) =>
  periodKey
    ? new Date(`${periodKey}-01T00:00:00Z`).toLocaleDateString("tr-TR", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

/**
 * Excel benzeri toplu mesai girişi.
 * Satırlar: personel, sütunlar: ana mesai nedenleri.
 * Hücreye tıklamak o personel için nedeni seçer; başlığa tıklamak
 * listede görünen tüm personele aynı nedeni uygular.
 * Kayıtlar mevcut tekli "createOvertimeEntry" endpoint'i ile tek tek oluşturulur.
 */
function OvertimeBulkGrid({ personnel, context, disabled, onSaved, onFeedback }) {
  const [workDate, setWorkDate] = useState(getLocalDate());
  const [defaultHours, setDefaultHours] = useState("");
  const [defaultWorkAreaId, setDefaultWorkAreaId] = useState("");
  const [rows, setRows] = useState({});
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dayStatus, setDayStatus] = useState({});
  const [warning, setWarning] = useState(null);
  const [showDistribution, setShowDistribution] = useState(true);
  const [expandedDepartments, setExpandedDepartments] = useState({});

  // Mesai tarihinin ayı: adil dağılım kontrolü bu ay üzerinden yapılır.
  const periodKey = /^\d{4}-\d{2}-\d{2}$/.test(workDate) ? workDate.slice(0, 7) : "";

  const loadDayStatus = useCallback(async () => {
    if (!periodKey) {
      setDayStatus({});
      return;
    }
    try {
      const response = await getOvertimePeriodStatus(`${periodKey}-01`);
      const map = {};
      (response.data.data.employees || []).forEach((item) => {
        map[item.employeeId] = item;
      });
      setDayStatus(map);
    } catch {
      // Uyarı verisi alınamazsa giriş engellenmez.
      setDayStatus({});
    }
  }, [periodKey]);

  useEffect(() => {
    loadDayStatus();
  }, [loadDayStatus]);

  const workAreas = context?.workAreas || [];

  const departments = useMemo(
    () =>
      [...new Set(personnel.map((employee) => employee.department).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b, "tr")
      ),
    [personnel]
  );

  const selectedCount = Object.keys(rows).length;
  // Seçim kalmadıysa "sadece seçilenler" filtresi listeyi boşaltmasın.
  const showOnlySelected = onlySelected && selectedCount > 0;

  const visiblePersonnel = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("tr-TR");
    return personnel.filter((employee) => {
      if (showOnlySelected && !rows[employee._id]) return false;
      if (departmentFilter && employee.department !== departmentFilter) return false;
      if (!term) return true;
      return `${employee.fullName} ${employee.position || ""} ${employee.employeeNumber || ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(term);
    });
  }, [personnel, search, departmentFilter, showOnlySelected, rows]);

  // Her alt neden tabloda ayrı bir sütun; ana neden üst başlıkta gruplanır.
  const reasonGroups = useMemo(
    () =>
      (context?.reasons || [])
        .map((reason) => ({ reason, details: context?.reasonDetails?.[reason.value] || [] }))
        .filter((group) => group.details.length),
    [context]
  );

  const buildRow = (current, reasonType, reasonDetailId) => ({
    hours: "",
    assignedWorkAreaId: "",
    note: "",
    customReason: "",
    ...current,
    reasonType,
    reasonDetailId: String(reasonDetailId),
    error: "",
  });

  const selectCell = (employeeId, reasonType, reasonDetailId) => {
    setRows((current) => ({
      ...current,
      [employeeId]: buildRow(current[employeeId], reasonType, reasonDetailId),
    }));
  };

  const toggleCell = (employeeId, reasonType, reasonDetailId) => {
    const current = rows[employeeId];
    if (current?.reasonDetailId === String(reasonDetailId)) {
      clearRow(employeeId);
      return;
    }
    // Bu ay zaten mesai almış personel ilk kez seçiliyorsa, adil dağılım için önce uyar.
    if (!current && dayStatus[employeeId]) {
      const employee = personnel.find((item) => item._id === employeeId);
      if (employee) {
        setWarning({ employee, reasonType, reasonDetailId });
        return;
      }
    }
    selectCell(employeeId, reasonType, reasonDetailId);
  };

  const applyReasonToVisible = (reason, detail) => {
    if (!visiblePersonnel.length) return;
    if (
      visiblePersonnel.length > 20 &&
      !window.confirm(
        `Listede görünen ${visiblePersonnel.length} personelin tamamına “${reason.label} → ${detail.label}” uygulansın mı?`
      )
    ) {
      return;
    }
    setRows((current) => {
      const next = { ...current };
      visiblePersonnel.forEach((employee) => {
        next[employee._id] = buildRow(next[employee._id], reason.value, detail._id);
      });
      return next;
    });
  };

  const monthlyHours = useCallback(
    (employeeId) => Number(dayStatus[employeeId]?.totalHours || 0),
    [dayStatus]
  );

  // Departman bazında bu ay mesai alan / almayan personel dağılımı.
  const distribution = useMemo(() => {
    const groups = {};
    personnel.forEach((employee) => {
      const department = employee.department || "Belirtilmemiş";
      if (departmentFilter && department !== departmentFilter) return;
      if (!groups[department]) groups[department] = { department, received: [], notReceived: [] };
      if (monthlyHours(employee._id) > 0) groups[department].received.push(employee);
      else groups[department].notReceived.push(employee);
    });
    return Object.values(groups)
      .map((group) => ({
        ...group,
        received: group.received.sort((a, b) => monthlyHours(b._id) - monthlyHours(a._id)),
        notReceived: group.notReceived.sort((a, b) => a.fullName.localeCompare(b.fullName, "tr")),
      }))
      .sort((a, b) => a.department.localeCompare(b.department, "tr"));
  }, [personnel, departmentFilter, monthlyHours]);

  const toggleDepartmentExpand = (department) =>
    setExpandedDepartments((current) => ({ ...current, [department]: !current[department] }));

  const focusEmployee = (employee) => {
    setSearch(employee.fullName);
    setOnlySelected(false);
  };

  // Aynı departmanda bu ay daha az mesai almış personel; en az alan en üstte.
  const suggestions = useMemo(() => {
    if (!warning) return [];
    const ownHours = monthlyHours(warning.employee._id);
    return personnel
      .filter(
        (employee) =>
          employee._id !== warning.employee._id &&
          employee.department === warning.employee.department &&
          !rows[employee._id] &&
          monthlyHours(employee._id) < ownHours
      )
      .sort(
        (a, b) =>
          monthlyHours(a._id) - monthlyHours(b._id) ||
          a.fullName.localeCompare(b.fullName, "tr")
      );
  }, [warning, personnel, rows, monthlyHours]);

  const chooseSubstitute = (employee) => {
    selectCell(employee._id, warning.reasonType, warning.reasonDetailId);
    setSearch("");
    onFeedback({
      type: "success",
      message: `${warning.employee.fullName} yerine ${employee.fullName} seçildi.`,
    });
    setWarning(null);
  };

  const confirmOriginal = () => {
    selectCell(warning.employee._id, warning.reasonType, warning.reasonDetailId);
    setWarning(null);
  };

  const updateRow = (employeeId, field, value) => {
    setRows((current) => ({
      ...current,
      [employeeId]: { ...current[employeeId], [field]: value, error: "" },
    }));
  };

  const clearRow = (employeeId) => {
    setRows((current) => {
      const next = { ...current };
      delete next[employeeId];
      return next;
    });
  };

  const clearAll = () => {
    if (selectedCount && !window.confirm("Tüm seçimler temizlensin mi?")) return;
    setRows({});
  };

  const validateRow = (row) => {
    const hours = Number(row.hours || defaultHours);
    if (!hours || hours < 0.25 || hours > 24) return "Saat 0,25 ile 24 arasında olmalı.";
    if (!(row.assignedWorkAreaId || defaultWorkAreaId)) return "Bölüm / hat seçin.";
    if (!row.reasonDetailId) return "Neden seçin.";
    if (row.reasonType === "OTHER" && !row.customReason.trim()) return "Diğer neden açıklaması yazın.";
    return "";
  };

  const handleSave = async () => {
    onFeedback(null);
    if (!workDate) {
      onFeedback({ type: "error", message: "Lütfen mesai tarihini seçin." });
      return;
    }
    const ids = Object.keys(rows);
    if (!ids.length) {
      onFeedback({ type: "error", message: "Tabloda en az bir personel için neden seçin." });
      return;
    }

    let hasError = false;
    const validated = {};
    ids.forEach((id) => {
      const error = validateRow(rows[id]);
      if (error) hasError = true;
      validated[id] = { ...rows[id], error };
    });
    if (hasError) {
      setRows(validated);
      setOnlySelected(true);
      onFeedback({
        type: "error",
        message: "Eksik bilgisi olan satırlar kırmızı ile işaretlendi. Lütfen tamamlayın.",
      });
      return;
    }

    setSaving(true);
    const results = {};
    const queue = [...ids];
    const worker = async () => {
      while (queue.length) {
        const id = queue.shift();
        const row = rows[id];
        try {
          await createOvertimeEntry({
            employeeId: id,
            workDate,
            hours: Number(row.hours || defaultHours),
            reasonType: row.reasonType,
            customReason: row.reasonType === "OTHER" ? row.customReason.trim() : "",
            assignedWorkAreaId: row.assignedWorkAreaId || defaultWorkAreaId,
            reasonDetailId: row.reasonDetailId,
            note: row.note.trim(),
          });
          results[id] = null;
        } catch (error) {
          results[id] = errorMessage(error);
        }
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(SAVE_CONCURRENCY, ids.length) }, worker)
    );

    const failedIds = ids.filter((id) => results[id]);
    const successCount = ids.length - failedIds.length;
    setRows((current) => {
      const next = {};
      failedIds.forEach((id) => {
        next[id] = { ...current[id], error: results[id] };
      });
      return next;
    });
    if (failedIds.length) {
      setOnlySelected(true);
    } else {
      setOnlySelected(false);
      setSearch("");
    }
    setSaving(false);
    loadDayStatus();

    onFeedback({
      type: failedIds.length ? "error" : "success",
      message: failedIds.length
        ? `${successCount} mesai kaydı oluşturuldu, ${failedIds.length} satırda hata var (tabloda işaretli).`
        : `${successCount} mesai kaydı başarıyla oluşturuldu.`,
    });
    if (successCount) await onSaved();
  };

  return (
    <div className="overtime-bulk">
      <div className="overtime-bulk-toolbar">
        <div className="overtime-field">
          <label htmlFor="bulkWorkDate">Mesai tarihi</label>
          <input
            id="bulkWorkDate"
            type="date"
            value={workDate}
            onChange={(event) => setWorkDate(event.target.value)}
            required
          />
        </div>
        <div className="overtime-field">
          <label htmlFor="bulkHours">Varsayılan süre (saat)</label>
          <input
            id="bulkHours"
            type="number"
            min="0.25"
            max="24"
            step="0.25"
            value={defaultHours}
            onChange={(event) => setDefaultHours(event.target.value)}
            placeholder="Örn. 2,5"
          />
        </div>
        <div className="overtime-field">
          <label htmlFor="bulkWorkArea">Varsayılan bölüm / hat</label>
          <select
            id="bulkWorkArea"
            value={defaultWorkAreaId}
            onChange={(event) => setDefaultWorkAreaId(event.target.value)}
          >
            <option value="">Bölüm / hat seçin</option>
            {workAreas.map((workArea) => (
              <option key={workArea._id} value={workArea._id}>
                {workArea.label}
              </option>
            ))}
          </select>
        </div>
        <div className="overtime-field overtime-bulk-search">
          <label htmlFor="bulkSearch">Personel ara</label>
          <div className="overtime-bulk-search-input">
            <Search size={15} />
            <input
              id="bulkSearch"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="İsim, görev veya sicil"
            />
          </div>
        </div>
        <div className="overtime-field">
          <label htmlFor="bulkDepartment">Departman</label>
          <select
            id="bulkDepartment"
            value={departmentFilter}
            onChange={(event) => setDepartmentFilter(event.target.value)}
          >
            <option value="">Tüm departmanlar</option>
            {departments.map((department) => (
              <option key={department} value={department}>
                {department}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overtime-bulk-hint">
        <span>
          Hücreye tıklayarak personelin mesai nedenini seçin. Neden başlığına tıklarsanız
          listede görünen herkese uygulanır. Saat ve bölüm boş bırakılırsa üstteki
          varsayılanlar kullanılır.
        </span>
        <label className="overtime-bulk-toggle">
          <input
            type="checkbox"
            checked={showOnlySelected}
            onChange={(event) => setOnlySelected(event.target.checked)}
          />
          Sadece seçilenleri göster
        </label>
      </div>

      <div className="overtime-dist">
        <button
          type="button"
          className="overtime-dist-toggle"
          onClick={() => setShowDistribution((current) => !current)}
        >
          <span>
            <strong>{formatMonth(periodKey)} mesai dağılımı</strong>
            <small>
              Departmanlara göre bu ay mesai alan ve almayan personel
              {departmentFilter ? ` · ${departmentFilter}` : ""}
            </small>
          </span>
          {showDistribution ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        {showDistribution && (
          <div className="overtime-dist-grid">
            {distribution.map((group) => {
              const total = group.received.length + group.notReceived.length;
              const expanded = expandedDepartments[group.department];
              const limit = expanded ? Infinity : DISTRIBUTION_CHIP_LIMIT;
              const hiddenCount =
                Math.max(0, group.received.length - DISTRIBUTION_CHIP_LIMIT) +
                Math.max(0, group.notReceived.length - DISTRIBUTION_CHIP_LIMIT);
              return (
                <div className="overtime-dist-card" key={group.department}>
                  <div className="overtime-dist-card-head">
                    <strong>{group.department}</strong>
                    <span>
                      {group.received.length} / {total} personel mesai aldı
                    </span>
                  </div>
                  <div className="overtime-chart-track">
                    <div
                      className="overtime-chart-fill overtime-chart-fill--count"
                      style={{ width: `${total ? (group.received.length / total) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="overtime-dist-cols">
                    <div>
                      <span className="overtime-dist-label received">
                        Mesai alanlar ({group.received.length})
                      </span>
                      <div className="overtime-dist-chips">
                        {group.received.slice(0, limit).map((employee) => (
                          <button
                            type="button"
                            key={employee._id}
                            className="overtime-dist-chip received"
                            onClick={() => focusEmployee(employee)}
                            title="Tabloda göster"
                          >
                            {employee.fullName}
                            <b>{formatHours(monthlyHours(employee._id))} sa</b>
                          </button>
                        ))}
                        {!group.received.length && <em>Henüz kimse almadı</em>}
                      </div>
                    </div>
                    <div>
                      <span className="overtime-dist-label waiting">
                        Almayanlar ({group.notReceived.length})
                      </span>
                      <div className="overtime-dist-chips">
                        {group.notReceived.slice(0, limit).map((employee) => (
                          <button
                            type="button"
                            key={employee._id}
                            className="overtime-dist-chip waiting"
                            onClick={() => focusEmployee(employee)}
                            title="Tabloda göster"
                          >
                            {employee.fullName}
                          </button>
                        ))}
                        {!group.notReceived.length && <em>Herkes en az bir kez aldı</em>}
                      </div>
                    </div>
                  </div>
                  {hiddenCount > 0 && (
                    <button
                      type="button"
                      className="overtime-dist-more"
                      onClick={() => toggleDepartmentExpand(group.department)}
                    >
                      {expanded ? "Daha az göster" : `+${hiddenCount} kişi daha göster`}
                    </button>
                  )}
                </div>
              );
            })}
            {!distribution.length && (
              <div className="overtime-empty">Gösterilecek personel yok.</div>
            )}
          </div>
        )}
      </div>

      <div className="overtime-bulk-wrap">
        <table className="overtime-bulk-table">
          <thead>
            <tr className="overtime-bulk-group-row">
              <th className="overtime-bulk-sticky" rowSpan={2}>
                Personel
              </th>
              {reasonGroups.map(({ reason, details }, index) => (
                <th
                  key={reason.value}
                  colSpan={details.length}
                  className={`overtime-bulk-group-head group-${index % 2}`}
                >
                  {reason.label}
                </th>
              ))}
              <th rowSpan={2}>Saat</th>
              <th rowSpan={2}>Bölüm / hat</th>
              <th rowSpan={2}>Not</th>
              <th rowSpan={2} aria-label="Temizle" />
            </tr>
            <tr className="overtime-bulk-detail-row">
              {reasonGroups.map(({ reason, details }, index) =>
                details.map((detail, detailIndex) => (
                  <th
                    key={detail._id}
                    className={`overtime-bulk-reason-head group-${index % 2} ${
                      detailIndex === 0 ? "group-start" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => applyReasonToVisible(reason, detail)}
                      disabled={disabled}
                      title={`${reason.label} → ${detail.label}: listede görünen herkese uygula`}
                    >
                      {detail.label}
                    </button>
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {visiblePersonnel.map((employee) => {
              const row = rows[employee._id];
              const existing = dayStatus[employee._id];
              return (
                <tr
                  key={employee._id}
                  className={`${row ? "is-selected" : ""} ${row?.error ? "has-error" : ""}`}
                >
                  <td className="overtime-bulk-sticky overtime-person-cell">
                    <strong>{employee.fullName}</strong>
                    <small>
                      {employee.department}
                      {employee.position ? ` · ${employee.position}` : ""}
                    </small>
                    {existing && (
                      <span
                        className="overtime-bulk-existing"
                        title={`Son mesai: ${formatDay(existing.lastWorkDate)}${
                          existing.requesterDepartments?.length
                            ? ` · Yazan: ${existing.requesterDepartments.join(", ")}`
                            : ""
                        }`}
                      >
                        <AlertTriangle size={12} /> Bu ay {formatHours(existing.totalHours)} sa ·{" "}
                        {existing.recordCount} kez
                      </span>
                    )}
                    {row?.error && <em className="overtime-bulk-error">{row.error}</em>}
                  </td>
                  {reasonGroups.map(({ reason, details }, index) =>
                    details.map((detail, detailIndex) => {
                      const active = row?.reasonDetailId === String(detail._id);
                      return (
                        <td
                          key={detail._id}
                          className={`overtime-bulk-cell group-${index % 2} ${
                            detailIndex === 0 ? "group-start" : ""
                          } ${active ? "active" : ""}`}
                          onClick={() =>
                            !disabled && toggleCell(employee._id, reason.value, detail._id)
                          }
                          title={`${reason.label} → ${detail.label}`}
                        >
                          {active ? (
                            <div className="overtime-bulk-cell-body">
                              <span className="overtime-bulk-mark" aria-label="Seçildi">
                                ✕
                              </span>
                              {reason.value === "OTHER" && (
                                <input
                                  value={row.customReason}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) =>
                                    updateRow(employee._id, "customReason", event.target.value)
                                  }
                                  maxLength={180}
                                  placeholder="Nedeni yazın"
                                  aria-label="Diğer neden"
                                />
                              )}
                            </div>
                          ) : (
                            <span className="overtime-bulk-dot" aria-hidden="true" />
                          )}
                        </td>
                      );
                    })
                  )}
                  <td>
                    <input
                      className="overtime-bulk-input overtime-bulk-hours"
                      type="number"
                      min="0.25"
                      max="24"
                      step="0.25"
                      value={row?.hours || ""}
                      onChange={(event) => updateRow(employee._id, "hours", event.target.value)}
                      placeholder={defaultHours || "Saat"}
                      disabled={!row}
                      aria-label="Mesai süresi"
                    />
                  </td>
                  <td>
                    <select
                      className="overtime-bulk-input"
                      value={row?.assignedWorkAreaId || ""}
                      onChange={(event) =>
                        updateRow(employee._id, "assignedWorkAreaId", event.target.value)
                      }
                      disabled={!row}
                      aria-label="Bölüm / hat"
                    >
                      <option value="">
                        {defaultWorkAreaId
                          ? `Varsayılan: ${
                              workAreas.find((item) => item._id === defaultWorkAreaId)?.label || ""
                            }`
                          : "Bölüm seçin"}
                      </option>
                      {workAreas.map((workArea) => (
                        <option key={workArea._id} value={workArea._id}>
                          {workArea.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="overtime-bulk-input"
                      value={row?.note || ""}
                      onChange={(event) => updateRow(employee._id, "note", event.target.value)}
                      maxLength={500}
                      placeholder="Not"
                      disabled={!row}
                      aria-label="Not"
                    />
                  </td>
                  <td>
                    {row && (
                      <button
                        type="button"
                        className="overtime-icon-btn"
                        onClick={() => clearRow(employee._id)}
                        title="Satırı temizle"
                      >
                        <Eraser size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!visiblePersonnel.length && (
          <div className="overtime-empty">
            Filtreyle eşleşen personel yok.
          </div>
        )}
      </div>

      {warning && (
        <div className="overtime-bulk-modal-backdrop" onClick={() => setWarning(null)}>
          <div
            className="overtime-bulk-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="overtime-bulk-modal-head">
              <div className="overtime-bulk-modal-icon">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h3>Bu personel bu ay mesai aldı</h3>
                <p>
                  <strong>{warning.employee.fullName}</strong> {formatMonth(periodKey)} içinde{" "}
                  <strong>{dayStatus[warning.employee._id]?.recordCount} kez</strong>, toplam{" "}
                  <strong>{formatHours(dayStatus[warning.employee._id]?.totalHours)} saat</strong>{" "}
                  mesai aldı. Mesainin adil dağılması için aynı departmandan daha az mesai
                  alan birine vermek ister misiniz?
                </p>
              </div>
              <button
                type="button"
                className="overtime-icon-btn"
                onClick={() => setWarning(null)}
                title="Kapat"
              >
                <X size={16} />
              </button>
            </div>

            <ul className="overtime-bulk-modal-entries">
              <li>
                Son mesai tarihi:{" "}
                <strong>{formatDay(dayStatus[warning.employee._id]?.lastWorkDate)}</strong>
                {dayStatus[warning.employee._id]?.requesterDepartments?.length > 0 && (
                  <small>
                    Mesaiyi yazan departman:{" "}
                    {dayStatus[warning.employee._id].requesterDepartments.join(", ")}
                  </small>
                )}
              </li>
            </ul>

            <div className="overtime-bulk-modal-section">
              <span>
                Aynı departmanda ({warning.employee.department}) bu ay daha az mesai alanlar
              </span>
              {suggestions.length ? (
                <div className="overtime-bulk-suggestions">
                  {suggestions.slice(0, SUGGESTION_LIMIT).map((employee) => (
                    <button
                      type="button"
                      key={employee._id}
                      onClick={() => chooseSubstitute(employee)}
                    >
                      <strong>{employee.fullName}</strong>
                      <small>
                        Bu ay {formatHours(monthlyHours(employee._id))} saat
                        {employee.position ? ` · ${employee.position}` : ""}
                      </small>
                    </button>
                  ))}
                  {suggestions.length > SUGGESTION_LIMIT && (
                    <em>
                      +{suggestions.length - SUGGESTION_LIMIT} kişi daha (tabloda departman
                      filtresiyle görebilirsiniz)
                    </em>
                  )}
                </div>
              ) : (
                <div className="overtime-empty">
                  Bu departmanda bu ay daha az mesai alan personel yok. Dağılım zaten dengeli
                  görünüyor.
                </div>
              )}
            </div>

            <div className="overtime-bulk-modal-actions">
              <button
                type="button"
                className="overtime-secondary-btn"
                onClick={() => setWarning(null)}
              >
                Vazgeç
              </button>
              <button type="button" className="overtime-primary-btn" onClick={confirmOriginal}>
                Yine de {warning.employee.fullName} için yaz
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overtime-bulk-footer">
        <span>
          <strong>{selectedCount}</strong> personel seçildi · {visiblePersonnel.length} /{" "}
          {personnel.length} personel listeleniyor
        </span>
        <div>
          <button
            type="button"
            className="overtime-secondary-btn"
            onClick={clearAll}
            disabled={saving || !selectedCount}
          >
            Seçimleri temizle
          </button>
          <button
            type="button"
            className="overtime-primary-btn"
            onClick={handleSave}
            disabled={saving || disabled || !selectedCount}
          >
            <Save size={16} />
            {saving ? "Kaydediliyor..." : `${selectedCount || ""} kaydı oluştur`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OvertimeBulkGrid;
